import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { activeNavHref } from '../utils/activeNavHref';

export interface SidebarFlyoutBadge {
    count: number;
    label: (count: number) => string;
}

export interface SidebarFlyoutSubItem {
    label: string;
    href: string;
    /** Not built yet - rendered but inert, with the "SOON" pill. */
    soon?: boolean;
    /** Count pill, shown only above zero. */
    badge?: SidebarFlyoutBadge;
}

export interface SidebarFlyoutProps {
    /** Section label, shown as the panel header. */
    label: string;
    /** Section icon, echoed in the header so the panel reads as "that item, expanded". */
    icon?: ReactNode;
    subItems: SidebarFlyoutSubItem[];
    /** Viewport coords of the collapsed nav item's right edge. */
    anchor: { left: number; top: number };
    /** Current route path - passed in so shared-ui stays router-agnostic. */
    currentPath: string;
    onSelect: (href: string) => void;
}

const FLYOUT_GAP_PX = 10;
const FLYOUT_VIEWPORT_MARGIN_PX = 12;
const ENTER_ANIMATION = 'odo-sidebar-flyout-in';

/** Portalled submenu for a collapsed sidebar; the host owns dismissal (see useAnchoredFlyout). */
export function SidebarFlyout({
    label,
    icon,
    subItems,
    anchor,
    currentPath,
    onSelect
}: SidebarFlyoutProps) {
    // Longest match wins, so a section index item ("Overview" → /resources)
    // does not stay lit while one of its children is open.
    const activeHref = activeNavHref(
        currentPath,
        subItems.map((subItem) => subItem.href)
    );

    return createPortal(
        <div
            data-sidebar-flyout
            role="group"
            aria-label={`${label} menu`}
            className="fixed z-50"
            style={{
                left: anchor.left + FLYOUT_GAP_PX,
                top: anchor.top,
                animation: `${ENTER_ANIMATION} 150ms ease-out`
            }}
        >
            {/* Self-contained enter animation - no consumer Tailwind config needed. */}
            <style>{`@keyframes ${ENTER_ANIMATION} { from { opacity: 0; transform: translateX(-6px); } to { opacity: 1; transform: translateX(0); } }`}</style>

            <div
                className="w-[216px] bg-white border border-gray-200 rounded-2xl overflow-hidden flex flex-col"
                style={{
                    maxHeight: `calc(100vh - ${anchor.top + FLYOUT_VIEWPORT_MARGIN_PX}px)`,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)'
                }}
            >
                {/* Header - echoes the nav item this panel expands */}
                <div className="flex items-center gap-2.5 px-3.5 pt-3 pb-2.5 border-b border-gray-100 flex-shrink-0">
                    {icon && (
                        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-600 flex-shrink-0">
                            {icon}
                        </span>
                    )}
                    <span className="text-sm font-semibold text-gray-800 truncate">{label}</span>
                </div>

                {/* Sub-items */}
                <div className="flex flex-col gap-0.5 p-1.5 overflow-y-auto scrollbar-hide">
                    {subItems.map((subItem) => {
                        const isSubActive = subItem.href === activeHref;
                        const badgeCount = subItem.badge?.count ?? 0;
                        return (
                            <button
                                key={subItem.href}
                                type="button"
                                onClick={() => !subItem.soon && onSelect(subItem.href)}
                                disabled={subItem.soon}
                                title={subItem.soon ? 'Coming soon' : undefined}
                                className={`flex h-9 items-center px-3 w-full rounded-lg transition-colors duration-200 ${
                                    subItem.soon ? 'cursor-not-allowed' : 'hover:bg-gray-100'
                                } ${isSubActive ? 'bg-gray-200' : ''}`}
                            >
                                <span
                                    className={`text-sm truncate min-w-0 ${
                                        subItem.soon
                                            ? 'font-medium text-gray-400'
                                            : isSubActive
                                              ? 'font-semibold text-gray-900'
                                              : 'font-medium text-gray-700'
                                    }`}
                                >
                                    {subItem.label}
                                </span>
                                {subItem.soon && (
                                    <span className="ml-auto text-[10px] font-semibold text-gray-400 border border-gray-200 rounded px-1 py-px">
                                        SOON
                                    </span>
                                )}
                                {badgeCount > 0 && (
                                    <span
                                        aria-label={subItem.badge?.label(badgeCount)}
                                        className="bg-yellow-400 text-gray-800 px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-auto"
                                    >
                                        {badgeCount}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body
    );
}
